import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Users,
  UserCheck,
  Clock,
  FileText,
  AlertTriangle,
  TrendingUp,
  RefreshCw,
  Calendar,
  Briefcase,
  Ticket,
} from "lucide-react";
import client from "../../src/api/client";
import "../styles/admin-base.css";
import "../styles/dashboard.css";
import { getActionDescription } from "../components/ai/AuditRow";
import DashboardCharts, { DashboardChartsProvider, DashboardChartsRow, DashboardChartsSidebar } from "./DashboardCharts";

const badgeClassByTone = {
  "is-green": "admin-chip is-green",
  "is-blue": "admin-chip is-blue",
  "is-purple": "admin-chip is-info",
  "is-orange": "admin-chip is-orange",
  "is-red": "admin-chip is-red",
};

export default function Dashboard({ onNavigate }) {
  const [dashboardData, setDashboardData] = useState({
    kpis: [],
    recentActivity: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshTick, setRefreshTick] = useState(300);
  const [activityView, setActivityView] = useState("grid");

  const iconMap = useMemo(
    () => ({
      users: Users,
      "user-check": UserCheck,
      "trending-up": TrendingUp,
      "alert-triangle": AlertTriangle,
      "file-text": FileText,
      clock: Clock,
      calendar: Calendar,
      briefcase: Briefcase,
      ticket: Ticket,
    }),
    [],
  );

  const activityViewOptions = [
    { id: "grid", label: "Grid" },
    { id: "list", label: "List" },
  ];

  const navigate = (target) => {
    if (typeof onNavigate === "function") {
      onNavigate(target);
    }
  };

  useEffect(() => {
    let subscribed = true;
    let countdownId;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await client.get("/api/admin/dashboard");
        if (subscribed) {
          setDashboardData(response.data ?? {});
          setLastUpdated(new Date());
          setRefreshTick(300);
        }
      } catch (err) {
        if (subscribed) {
          setError(err?.response?.data?.message ?? "Unable to load dashboard data.");
        }
      } finally {
        if (subscribed) {
          setLoading(false);
        }
      }
    };

    fetchData();
    const intervalId = setInterval(fetchData, 5 * 60 * 1000);

    countdownId = setInterval(() => {
      setRefreshTick((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => {
      subscribed = false;
      clearInterval(intervalId);
      clearInterval(countdownId);
    };
  }, []);

  const handleRefresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setRefreshTick(300);
      const response = await client.get("/api/admin/dashboard");
      setDashboardData(response.data ?? {});
      setLastUpdated(new Date());
    } catch (err) {
      setError(err?.response?.data?.message ?? "Unable to load dashboard data.");
    } finally {
      setLoading(false);
    }
  }, []);

  const formatNumber = (value) => {
    if (value === null || value === undefined) return "0";
    if (typeof value === "string") return value;
    return new Intl.NumberFormat("en-PH").format(value);
  };

  const refreshCountdown = useMemo(() => {
    const minutes = Math.floor(refreshTick / 60);
    const seconds = refreshTick % 60;
    if (refreshTick <= 0) return "refreshing…";
    if (minutes > 0) return `${minutes}m ${seconds.toString().padStart(2, "0")}s`;
    return `${seconds}s`;
  }, [refreshTick]);

  const lastUpdatedLabel = useMemo(() => {
    if (!lastUpdated) return null;
    return lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }, [lastUpdated]);

  const computeTime = (timestamp, fallback) => {
    if (fallback) return fallback;
    if (!timestamp) return "";
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return "";
    const diffMs = Date.now() - date.getTime();
    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;
    if (diffMs < hour) {
      const minutes = Math.max(1, Math.round(diffMs / minute));
      return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
    }
    if (diffMs < day) {
      const hours = Math.round(diffMs / hour);
      return `${hours} hour${hours === 1 ? "" : "s"} ago`;
    }
    const days = Math.round(diffMs / day);
    return `${days} day${days === 1 ? "" : "s"} ago`;
  };

  const parseActivityMetadata = (detail) => {
    if (!detail) {
      return {};
    }
    if (typeof detail === "object") {
      return detail;
    }
    if (typeof detail === "string") {
      try {
        const parsed = JSON.parse(detail);
        if (parsed && typeof parsed === "object") {
          return parsed;
        }
      } catch (_error) {
        return { description: detail };
      }
      return { description: detail };
    }
    return {};
  };

  const formatActionLabel = (action) => {
    if (!action) return "Unknown Action";
    return String(action)
      .replace(/[-_]/g, " ")
      .replace(/\b\w/g, (character) => character.toUpperCase());
  };

  const toDisplayRole = (role) => {
    if (!role || typeof role !== "string") {
      return "Admin";
    }

    const normalized = role.trim().toLowerCase();
    if (normalized.includes("super")) return "Super Admin";
    if (normalized.includes("ticket")) return "Ticket Admin";
    if (normalized.includes("approver") || normalized.includes("approval")) return "Approver Admin";
    if (normalized.includes("booking")) return "Booking Admin";
    if (normalized.includes("event")) return "Events Admin";
    if (normalized.includes("id card") || normalized.includes("id_card")) return "ID Card Admin";

    return role
      .trim()
      .replace(/[_-]/g, " ")
      .replace(/\b\w/g, (character) => character.toUpperCase());
  };

  const { kpis = [], recentActivity = [] } = dashboardData;

  return (
    <div className="admin-page dashboard-page">
      <header className="admin-surface dashboard-hero">
        <div className="dashboard-hero__intro">
          <span className="dashboard-hero__eyebrow">Operations Overview</span>
          <h1>Admin Dashboard</h1>
          <p className="admin-muted">
            Welcome to LaborConnect Admin. Monitor member growth and operations at a glance.
          </p>
        </div>
        <div className="admin-actions dashboard-hero__actions">
          <button
            type="button"
            className={`admin-icon-button admin-icon-button--refresh ${loading ? "is-loading" : ""}`}
            onClick={handleRefresh}
            disabled={loading}
            aria-label="Refresh dashboard"
            title="Refresh dashboard"
            aria-busy={loading}
          >
            <RefreshCw size={18} />
          </button>
          <button
            type="button"
            className="admin-button is-primary"
            onClick={() => navigate("members")}
          >
            View All Members
          </button>
          <button
            type="button"
            className="admin-button"
            onClick={() => navigate("approvals")}
          >
            Review Pending
          </button>
          <div className="dashboard-hero__meta dashboard-hero__meta--actions">
            {lastUpdatedLabel ? (
              <span className="admin-pill dashboard-hero__meta-pill">Updated {lastUpdatedLabel}</span>
            ) : null}
            <span className="admin-pill dashboard-hero__meta-pill">Auto refresh in {refreshCountdown}</span>
          </div>
        </div>
      </header>

      {error ? (
        <section className="admin-surface dashboard-alert-surface">
          <div className="admin-empty-state dashboard-alert">{error}</div>
        </section>
      ) : null}

      <DashboardChartsProvider>
        <div className="dashboard-body">
        <div className="dashboard-body__main">
        <div className="admin-card-grid admin-card-grid--kpi">
          {kpis
            .filter((item) => {
              const id = String(item.id ?? "").toLowerCase();
              return !id.includes("dues") && !id.includes("finance");
            })
            .map((item) => {
            const Icon = iconMap[item.icon] ?? Users;
            return (
              <article key={item.id ?? item.title} className="admin-card dashboard-kpi-card">
                <div className="admin-card__label">{item.title}</div>
                <div className="admin-row dashboard-kpi-value-row">
                  <div className="admin-card__value">{formatNumber(item.value)}</div>
                  <span className={badgeClassByTone[item.tone] || "admin-chip"}>
                    <Icon size={16} />
                  </span>
                </div>
                {item.subStats?.length ? (
                  <div className="dashboard-kpi-substats">
                    {item.subStats.map((stat) => (
                      <span key={stat.label} className="dashboard-kpi-substat">
                        <strong>{formatNumber(stat.value)}</strong> {stat.label}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="admin-card__meta">{item.change}</div>
                )}
              </article>
            );
          })}
          {loading && kpis.length === 0
            ? Array.from({ length: 3 }).map((_, index) => (
                <article key={`kpi-skeleton-${index}`} className="admin-card">
                  <div className="admin-card__label dashboard-kpi-skeleton-label">
                    Loading metric…
                  </div>
                  <div className="admin-row dashboard-kpi-skeleton-row">
                    <div className="admin-card__value">—</div>
                    <span className="admin-chip">…</span>
                  </div>
                  <div className="admin-card__meta dashboard-kpi-skeleton-meta">
                    Fetching details
                  </div>
                </article>
              ))
            : null}
          {!loading && kpis.length === 0 ? (
            <article className="admin-card dashboard-kpi-empty">
              <div className="admin-card__label">No data</div>
              <div className="admin-card__meta">Add members to see insights.</div>
            </article>
          ) : null}
        </div>

        <DashboardChartsRow />
        </div>

        <DashboardChartsSidebar />
        </div>
      </DashboardChartsProvider>

      <section className="admin-surface dashboard-section dashboard-section--activity">
        <div className="dashboard-section__head dashboard-section__head--activity">
          <div>
            <h2>Recent Activity</h2>
            <p className="admin-muted dashboard-section__subtext">
              Latest admin actions and system updates in one stream.
            </p>
          </div>
          <div className="dashboard-activity__controls">
            <div className="dashboard-activity__view-toggle" role="tablist" aria-label="Activity view mode">
              {activityViewOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={`dashboard-activity__toggle-chip ${activityView === option.id ? "is-active" : ""}`}
                  onClick={() => setActivityView(option.id)}
                  aria-pressed={activityView === option.id}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="admin-button"
              onClick={() => navigate("audit")}
            >
              View All
            </button>
          </div>
        </div>
        <div className={`admin-activity-grid admin-activity-grid--${activityView}`}>
          {recentActivity.map((activity, index) => (
            (() => {
              const metadata = parseActivityMetadata(activity.detail);
              const actionLabel = formatActionLabel(activity.action);
              const adminLabel = activity.adminName
                || metadata.actorName
                || metadata.adminName
                || "Admin User";
              const roleFromArray = Array.isArray(activity.adminRoles) && activity.adminRoles.length > 0
                ? activity.adminRoles[0]
                : null;
              const adminRoleTitle = toDisplayRole(
                activity.adminRole
                || roleFromArray
                || metadata.actorRole
                || metadata.role
                || "Admin",
              );
              const detailText = getActionDescription({
                action: activity.action,
                metadata,
                entityId: activity.entityId,
                ticketId: metadata.ticketId ?? null,
                reason: metadata.reason ?? null,
                description: metadata.description ?? null,
              }) || activity.subject || "No additional details.";

              return (
                <article
                  key={`${activity.action}-${activity.createdAt ?? index}`}
                  className={`admin-card dashboard-activity-card ${activity.tone ? `dashboard-activity-card--${activity.tone}` : ""}`}
                >
                  <div className="dashboard-activity-card__header">
                    <strong className="dashboard-activity-card__title">
                      {adminRoleTitle}
                    </strong>
                    <span
                      className={`${badgeClassByTone[activity.tone] || "admin-chip"} dashboard-activity-card__time`}
                    >
                      {computeTime(activity.createdAt, activity.time)}
                    </span>
                  </div>

                  <div className="dashboard-activity-card__fields">
                    <div className="dashboard-activity-card__field">
                      <span className="dashboard-activity-card__label">Admin</span>
                      <span className="dashboard-activity-card__value">{adminLabel}</span>
                    </div>

                    <div className="dashboard-activity-card__field">
                      <span className="dashboard-activity-card__label">Action</span>
                      <span className="dashboard-activity-card__value">{actionLabel}</span>
                    </div>

                    <div className="dashboard-activity-card__field dashboard-activity-card__field--detail">
                      <span className="dashboard-activity-card__label">Detail</span>
                      <p className="dashboard-activity-card__desc">{detailText}</p>
                    </div>
                  </div>
                </article>
              );
            })()
          ))}
          {!loading && recentActivity.length === 0 ? (
            <div className="admin-card-grid__placeholder admin-empty-state">
              No recent activity yet.
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}

Dashboard.propTypes = {
  onNavigate: PropTypes.func,
};
